
<template>
  <div class="u-code-input">
    <div
      class="u-code-input__item"
      :style="[itemStyle(index)]"
      v-for="(item, index) in codeLength"
      :key="index"
    >
      <div class="u-code-input__item__dot" v-if="dot && codeArray.length > index"></div>
      <span v-else class="u-input_active_title">{{ codeArray[index] }}</span>
      <div
        class="u-code-input__item__line"
        :class="{
          'u-code-input__item__line-4': maxlength == 4,
          'u-code_input_active': codeArray[index] || index === 0,
        }"
        v-if="mode === 'line'"
      ></div>
      <div
        v-if="isFocus && codeArray.length === index"
        :style="{ backgroundColor: color }"
        class="u-code-input__item__cursor"
      ></div>
    </div>
    <input
      type="number"
      :focus="true"
      :value="inputValue"
      :maxlength="maxlength"
      :adjustPosition="adjustPosition"
      class="u-code-input__input"
      @input="inputHandler"
      :style="{
        height: addUnit(size)
      }"
      @focus="isFocus = true"
      @blur="isFocus = false"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch,nextTick, defineProps, defineEmits } from 'vue';


export default defineComponent({
  name: 'UCodeInput',
  props: {
    adjustPosition: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: [String, Number],
      default: 4
    },
    dot: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: ''
    },
    hairline: {
      type: Boolean,
      default: false
    },
    space: {
      type: [String, Number],
      default: 10
    },
    modelValue: {
       type: [String, Number],
       default: ''
    },
    focus: {
      type: Boolean,
      default: true
    },
    bold: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'rgb(96,98,102)'
    },
    fontSize: {
      type: [String, Number],
      default: 18
    },
    size: {
      type: [String, Number],
      default: 40
    },
    disabledKeyboard: {
      type: Boolean,
      default: false
    },
    borderColor: {
      type: String,
      default: ''
    },
    disabledDot: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const inputValue = ref(String(props.modelValue).substring(0, props.maxlength));
    const isFocus = ref(props.focus);

    // Watch for changes in `value` prop
    watch(
      () => props.modelValue,
      (newValue) => {
        inputValue.value = String(newValue).substring(0, props.maxlength);
      },
      { immediate: true }
    );

    // Computed properties
    const codeLength = computed(() => {
      const length = Number(props.maxlength);
      return length > 0 ? new Array(length) : [];
    });

    const codeArray = computed(() => String(inputValue.value).split(''));

    const itemStyle = (index: number) => {
      const style: {[key:string]:any} = {
        width: props.maxlength === 4 ? '40px' : '60px',
        height: '60px',
      };

      if (props.mode === 'box') {
        style.border = `${props.hairline ? 0.5 : 1}px solid ${props.borderColor}`;
        if (getPx(props.space as string) === 0) {
          if (index === 0) {
            style.borderTopLeftRadius = '3px';
            style.borderBottomLeftRadius = '3px';
          }
          if (index === codeLength.value.length - 1) {
            style.borderTopRightRadius = '3px';
            style.borderBottomRightRadius = '3px';
          }
          if (index !== codeLength.value.length - 1) {
            style.borderRight = 'none';
          }
        }
      }

      if (index !== codeLength.value.length - 1) {
        style.marginRight = addUnit(props.space as string);
      } else {
        style.marginRight = 0;
      }

      return style;
    };

    // Methods
    const inputHandler = (e) => {
      let value = e.target.value;
      inputValue.value = value;

      if (props.disabledDot) {
        nextTick(() => {
          inputValue.value = value.replace('.', '');
        });
      }

      emit('update:modelValue', value); // Change this
      emit('change', value);


      if (String(value).length >= Number(props.maxlength)) {
        emit('finish', value);
      }
    };

    const getPx = (value:string, unit = false) => {
      if (/^[\+-]?(\d+\.?\d*|\.\d+|\d\.\d+e\+\d+)$/.test(value)) {
        return unit ? `${value}px` : Number(value);
      }
      return unit ? `${parseInt(value)}px` : parseInt(value);
    };

    const addUnit = (value = 'auto', unit = 'px') => {
      value = String(value);
      return /^[\+-]?(\d+\.?\d*|\.\d+|\d\.\d+e\+\d+)$/.test(value) ? `${value}${unit}` : value;
    };

    return {
      inputValue,
      isFocus,
      codeLength,
      codeArray,
      itemStyle,
      inputHandler,
      getPx,
      addUnit
    };
  }
});
</script>
  <style scoped lang="less">
  .u-code-input {
    display: flex;
    flex-direction: row;
    position: relative;
    overflow: hidden;
    justify-content: space-between;
    width: 100%;
  }
  
  .u-code-input .u-code-input__item {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  
  .u-code-input .u-code-input__item .u-code-input__item__line {
    position: absolute;
    bottom: 0;
    height: 1px;
    border-radius: 100px;
    width: 40px;
    background-color: var(--color_shadow1);
  }
  .u-code-input .u-code-input__item .u-code-input__item__line-4 {
    width: 60px;
  }
  .u-code-input .u-code-input__item .u-code_input_active {
    background-color: var(--color_text1) !important;
  }
  
  .u-code-input .u-code-input__item .u-code-input__item__dot {
    position: absolute;
    bottom: 0;
    height: 1px;
    border-radius: 100px;
    width: 40px;
    background-color: var(--color_shadow1);
  }
  
  .u-code-input .u-code-input__item .u-code-input__item__cursor {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 1px;
    height: 40%;
    animation: 1s u-cursor-flicker infinite;
  }
  
  .u-code-input .u-code-input__input {
    position: absolute;
    left: -375px;
    width: 750px;
    top: 0;
    background-color: transparent;
    span-align: left;
    -webkit-appearance: none;
  }
  .u-input_active_title {
    font-size: 28px;
    color: var(--color_text1);
    line-height: 26px;
    font-family: DIN Medium, DIN Medium;
  }
  
  /* #ifndef APP-PLUS */
  @keyframes u-cursor-flicker {
    0% {
      opacity: 0;
    }
  
    50% {
      opacity: 1;
    }
  
    100% {
      opacity: 0;
    }
  }
  /* #endif */
  input {
    caret-color:transparent !important;
  }

  // #ifdef H5
  input {
    background: transparent;
    -webkit-appearance: none;
    opacity: 0;
  }
  .u-code-input .u-code-input__input {
    opacity: 0;
  }
  // #endif
  </style>